// Estado de la licencia en el dispositivo, a partir del último confirmado por el servidor.
// Función pura (testeable): el estado avanza por fechas aunque no haya conexión.
//
// Etapa 8B: si el servidor entregó una credencial firmada (y su firma se verificó), manda la
// credencial: sus fechas, su vencimiento (7 días) y el reloj protegido del dispositivo. Sin
// credencial (servidor sin la clave configurada), se usa el estado guardado como en la 8A.
import {
  CLOCK_TOLERANCE_MS,
  canWriteWith,
  licenseSchedule,
  trustedNow,
  type DeviceClock,
  type LicenseSchedule,
  type LicenseStatus,
  type LicenseTokenPayload,
} from '@pos/core';
import type { CachedLicense } from '@pos/local-db';

/** Días sin poder verificar la licencia antes de pedir conexión (sin credencial firmada). */
export const MAX_OFFLINE_DAYS = 10;

export type DeviceLicenseState =
  | { kind: 'unknown' }
  /** Hace demasiado que no se puede verificar (o la credencial guardada no es válida): hay que conectarse. */
  | { kind: 'stale'; lastCheck: Date }
  /** El reloj del dispositivo está atrasado respecto de lo que ya vio: hay que corregirlo o conectarse. */
  | { kind: 'clock' }
  | {
      kind: 'ok';
      status: LicenseStatus;
      schedule: LicenseSchedule;
      canWrite: boolean;
      license: CachedLicense;
      /** Credencial con la que se hacen los cambios (viaja con cada operación). */
      jti: string | null;
    };

export interface LicenseInputs {
  /** Hora del dispositivo. */
  now?: Date;
  /**
   * Contenido de la credencial ya verificado: undefined = todavía no se verificó (se usa el
   * estado guardado), null = la credencial guardada no es válida.
   */
  verified?: LicenseTokenPayload | null;
  clock?: DeviceClock;
}

export function deviceLicenseState(cached: CachedLicense | undefined, inputs: LicenseInputs = {}): DeviceLicenseState {
  const deviceNow = inputs.now ?? new Date();
  if (!cached) return { kind: 'unknown' };

  if (cached.token && inputs.verified !== undefined) {
    const token = inputs.verified;
    if (!token) return { kind: 'stale', lastCheck: new Date(cached.fetched_at) };
    const { now, rolledBack } = inputs.clock ? trustedNow(inputs.clock, deviceNow.getTime()) : { now: deviceNow.getTime(), rolledBack: false };
    if (rolledBack || now < token.iat * 1000 - CLOCK_TOLERANCE_MS) return { kind: 'clock' };
    if (now > token.exp * 1000) return { kind: 'stale', lastCheck: new Date(token.iat * 1000) };
    const schedule = licenseSchedule(token.lic, new Date(now));
    return { kind: 'ok', status: schedule.status, schedule, canWrite: canWriteWith(schedule.status), license: cached, jti: token.jti };
  }

  const fetched = new Date(cached.fetched_at);
  if (deviceNow.getTime() - fetched.getTime() > MAX_OFFLINE_DAYS * 86_400_000) return { kind: 'stale', lastCheck: fetched };
  // El reloj del dispositivo puede estar corrido: se usa la diferencia con la hora del servidor
  // que se vio al confirmar (si el dispositivo atrasa, igual avanza).
  const skew = new Date(cached.server_time).getTime() - fetched.getTime();
  const serverNow = new Date(deviceNow.getTime() + skew);
  const schedule = licenseSchedule(
    {
      paid_until: cached.paid_until,
      trial_until: cached.trial_until,
      grace_days: cached.grace_days,
      readonly_days: cached.readonly_days,
      manual_override: (cached.manual_override as LicenseStatus | null) ?? null,
      no_expiry: cached.no_expiry ?? false,
    },
    serverNow,
  );
  return { kind: 'ok', status: schedule.status, schedule, canWrite: canWriteWith(schedule.status), license: cached, jti: null };
}

/** ¿Se puede cargar datos? Sin información todavía (primer uso), se deja: el servidor decide igual. */
export function canWriteState(state: DeviceLicenseState): boolean {
  return state.kind === 'unknown' || (state.kind === 'ok' && state.canWrite);
}
